/**
 * 👍 / 👎 vote buttons + optional comment for thumbs-down.
 *
 * - One vote per user per article (backend upserts).
 * - Re-voting flips the count.
 * - On 👎, a small inline textarea appears so the user can explain ("missing step…").
 * - Counters refresh from the server response.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ThumbsUp, ThumbsDown, Check } from "lucide-react";
import { toast } from "sonner";

export default function KbVoteButtons({ articleId, initialHelpful = 0, initialUnhelpful = 0, compact = false }) {
  const [helpful, setHelpful] = useState(initialHelpful);
  const [unhelpful, setUnhelpful] = useState(initialUnhelpful);
  const [myVote, setMyVote] = useState(null);   // true | false | null
  const [comment, setComment] = useState("");
  const [showComment, setShowComment] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);

  // Load my prior vote on mount
  useEffect(() => {
    let cancelled = false;
    api.get(`/kb/${articleId}/vote`).then((r) => {
      if (cancelled) return;
      if (r.data?.helpful === true || r.data?.helpful === false) {
        setMyVote(r.data.helpful);
        if (r.data.helpful === false && r.data.comment) setComment(r.data.comment);
      }
    }).catch(() => {/* silent */});
    return () => { cancelled = true; };
  }, [articleId]);

  const vote = useCallback(async (helpfulFlag) => {
    try {
      const r = await api.post(`/kb/${articleId}/vote`, { helpful: helpfulFlag, comment: helpfulFlag ? "" : comment });
      setHelpful(r.data.helpful_count);
      setUnhelpful(r.data.unhelpful_count);
      setMyVote(helpfulFlag);
      if (helpfulFlag) {
        setShowComment(false);
        toast.success("Thanks for the feedback!");
      } else {
        setShowComment(true);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Vote failed");
    }
  }, [articleId, comment]);

  const submitComment = async () => {
    setSubmittingComment(true);
    try {
      const r = await api.post(`/kb/${articleId}/vote`, { helpful: false, comment });
      setHelpful(r.data.helpful_count);
      setUnhelpful(r.data.unhelpful_count);
      setShowComment(false);
      toast.success("Saved — thanks for the detail!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSubmittingComment(false); }
  };

  const upActive = myVote === true;
  const downActive = myVote === false;

  return (
    <div className={compact ? "" : "border-t border-slate-100 pt-3 mt-3"} data-testid="kb-vote">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] text-slate-500 mr-1">Was this helpful?</span>
        <Button
          type="button" variant="outline" size="sm"
          onClick={() => vote(true)}
          className={`h-8 text-xs ${upActive ? "bg-emerald-50 border-emerald-300 text-emerald-700" : ""}`}
          data-testid="kb-vote-up"
        >
          <ThumbsUp className="w-3.5 h-3.5 mr-1" /> {helpful}
          {upActive && <Check className="w-3 h-3 ml-1 text-emerald-600" />}
        </Button>
        <Button
          type="button" variant="outline" size="sm"
          onClick={() => vote(false)}
          className={`h-8 text-xs ${downActive ? "bg-rose-50 border-rose-300 text-rose-700" : ""}`}
          data-testid="kb-vote-down"
        >
          <ThumbsDown className="w-3.5 h-3.5 mr-1" /> {unhelpful}
          {downActive && <Check className="w-3 h-3 ml-1 text-rose-600" />}
        </Button>
        {downActive && !showComment && (
          <button type="button" onClick={() => setShowComment(true)}
                  className="text-[11px] text-rose-600 hover:text-rose-700 underline">
            Add detail
          </button>
        )}
      </div>
      {showComment && downActive && (
        <div className="mt-2 space-y-2" data-testid="kb-vote-comment-box">
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="What's missing or unclear? (Visible only to KB editors.)"
            rows={2}
            className="text-xs"
            data-testid="kb-vote-comment-input"
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" className="h-7 text-xs"
                    onClick={() => setShowComment(false)}>Cancel</Button>
            <Button size="sm" className="bg-rose-600 hover:bg-rose-700 text-white h-7 text-xs"
                    onClick={submitComment} disabled={submittingComment}
                    data-testid="kb-vote-comment-submit">
              {submittingComment ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
